// Google Analytics Measurement ID
export const GA_MEASUREMENT_ID = 'G-MH6DLELT6E'; // MovieHub GA4 ID


let isInitialized = false;
let initializationPromise = null;

// Development-only logging
const logDev = (...args) => {
  if (import.meta.env.DEV) {
    console.log(...args);
  }
};

const warnDev = (...args) => {
  if (import.meta.env.DEV) {
    console.warn(...args);
  }
};

const errorDev = (...args) => {
  if (import.meta.env.DEV) {
    console.error(...args);
  }
};

// Initialize Google Analytics
export const initGA = () => {
  // Return existing initialization promise if already initializing
  if (initializationPromise) {
    return initializationPromise;
  }

  // Return resolved promise if already initialized
  if (isInitialized) {
    logDev('📊 GA already initialized');
    return Promise.resolve();
  }

  initializationPromise = new Promise((resolve, reject) => {
    try {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      
      // Wait for script to load
      script.onload = () => {
        window.dataLayer = window.dataLayer || [];
        function gtag() {
          logDev('📊 GA Event:', ...arguments);
          window.dataLayer.push(arguments);
        }
        window.gtag = gtag;  // Make gtag globally available
        gtag('js', new Date());
        
        // Enhanced measurement settings
        gtag('config', GA_MEASUREMENT_ID, {
          send_page_view: true,
          debug_mode: import.meta.env.DEV,  // Only enable debug mode in development
          custom_map: {
            'dimension1': 'environment',
            'dimension2': 'user_type',
            'dimension3': 'screen_resolution',
            'dimension4': 'connection_type'
          },
          allow_google_signals: true,
          allow_ad_personalization_signals: true,
          anonymize_ip: true
        });
        
        // Set environment
        gtag('set', 'environment', import.meta.env.MODE);
        
        // Set additional user information
        const userInfo = {
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          connection_type: navigator.connection ? navigator.connection.effectiveType : 'unknown',
          user_type: localStorage.getItem('user_type') || 'visitor'
        };
        
        gtag('set', userInfo);

        // Send initialization event with user info
        gtag('event', 'ga_initialization_test', {
          event_category: 'system',
          event_label: 'GA Initialization Test',
          environment: import.meta.env.MODE,
          ...userInfo
        });

        logDev('🚀 Google Analytics initialized with ID:', GA_MEASUREMENT_ID, 'Environment:', import.meta.env.MODE);
        logDev('📍 User Info:', userInfo);
        isInitialized = true;
        resolve();
      };

      script.onerror = (error) => {
        errorDev('❌ Error loading Google Analytics script:', error);
        reject(error);
      };

      document.head.appendChild(script);
    } catch (error) {
      errorDev('❌ Error initializing Google Analytics:', error);
      reject(error);
    }
  }).finally(() => {
    initializationPromise = null;
  });

  return initializationPromise;
};

// Track page views with enhanced data
export const trackPageView = async (path) => {
  try {
    await initGA();
    if (typeof window.gtag === 'function') {
      logDev('📄 GA Page View:', path);
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: path,
        page_title: document.title,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        connection_type: navigator.connection ? navigator.connection.effectiveType : 'unknown'
      });
    }
  } catch (error) {
    errorDev('❌ Error tracking page view:', error);
  }
};

// Track events with location data
export const trackEvent = async (eventName, eventParams = {}) => {
  try {
    await initGA();
    if (typeof window.gtag === 'function') {
      // Add user timezone
      const enhancedParams = {
        ...eventParams,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        connection_type: navigator.connection ? navigator.connection.effectiveType : 'unknown'
      };
      
      logDev('🎯 GA Event:', eventName, enhancedParams);
      window.gtag('event', eventName, enhancedParams);
    }
  } catch (error) {
    errorDev('❌ Error tracking event:', error);
  }
};
